<template>
  <div>
    <div class="panel-title">
      Accounts
    </div>
    <div class="line-separator"></div>
      <div v-if="accounts && accounts.length === 0" class="panel-body">
        <div class="alert alert-info alert-dismissible" role="alert">
            No accounts found.
        </div>
    </div>
    <div v-if="accounts && accounts.length > 0" v-bind:class="{'table-fixed-header--sm cols-5': accounts.length > 3 || fetchingSummaryReports}"
      class="table-responsive bottom-margin">
      <spinner class="table-spinner" v-if="fetchingSummaryReports" ></spinner>
      <table class="table table-striped" v-if="!fetchingSummaryReports">
        <thead>
          <tr>
            <th class="text-center">Account name</th>
            <th class="text-center">Type of account</th>
            <th class="text-center">Account Balance</th>
            <th class="text-center">Default Account</th>
            <th class="text-center last-column-title">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="account in accounts" :key="account.id">
            <td class="text-center">{{account.accountName}}</td>
            <td class="text-center">{{account.accountType}}</td>
            <td class="text-center">{{formatAmount(account.currentBalance)}}</td>
            <td class="text-center">
              <div
                :class="{ 'radio-item': true, 'radio-item-unselected': account.id !== defaultAccountId }"
              >
                <input
                  type="radio"
                  :id="account.id"
                  :for="account.id"
                  :value="account.id"
                  @click="changeDefaultAccount(account)"
                />
                <label :for="account.id" class="lbl-defaultAccount" />
              </div>
            </td>
            <td class="text-center last-column-data">
              <button type="button" class="btn btn-details btn-outline" v-on:click="goToViewDetail(account)">View details</button>
              <button type="button" class="btn btn-details btn-outline" v-on:click="generateSummaryReport(account)" >Summary Report</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="row">
      <div class="col-md-12 top-margin">
        <button type="submit" class="btn btn-default btn-lg btn-new"
          v-on:click="openAccountCreationModal()">Create new</button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  mapMutations,
  mapState,
  mapActions,
} from 'vuex';

import {
  SHOW_ACCOUNT_CREATION_MODAL,
  SHOW_QUESTION_MODAL,
  HIDE_QUESTION_MODAL,
} from '@/store/mutation-types';

import {
  getAccounTypeProperties,
  formatAmount,
  downloadPDF,
} from '@/util';

import spinner from '@/components/widgets/Spinner';

import { Constants } from '../data/static/constants';

const mix = {
  methods: {
    formatAmount,
  },
};

export default {
  name: 'AccountsList',
  props: ['accounts', 'userType', 'defaultAccountId'],
  beforeMount() {
    this.initializeState();
  },
  computed: {
    ...mapState('app', [
      'userTypes',
    ]),
  },
  mixins: [mix],
  data() {
    return {
      userId: '',
      name: '',
      showDefaultAccountChanged: false,
      fetchingSummaryReports: false,
    };
  },
  methods: {
    ...mapActions('account', ['getAccountSummaryReport']),
    ...mapMutations('accountCreation', {
      showAccountCreationModal: SHOW_ACCOUNT_CREATION_MODAL,
    }),
    ...mapMutations('question', {
      hideQuestionModal: HIDE_QUESTION_MODAL,
      showQuestionModal: SHOW_QUESTION_MODAL,
    }),
    initializeState() {
      this.userId = this.$route.params.userId;
      this.name = this.$route.params.name;
    },
    openAccountCreationModal() {
      const userId = this.userId;
      const name = this.name;
      this.showAccountCreationModal({ userId, name });
    },
    async generateSummaryReport(account) {
      this.fetchingSummaryReports = true;
      const withoutAccruedInterestReport = await this.getAccountSummaryReport({
        accountId: account.id,
        withAccruedInterest: false,
      });
      downloadPDF(withoutAccruedInterestReport.fileName, withoutAccruedInterestReport.pdf.data);
      const withAccruedInterest = await this.getAccountSummaryReport({
        accountId: account.id,
        withAccruedInterest: true,
      });
      downloadPDF(withAccruedInterest.fileName, withAccruedInterest.pdf.data);
      this.fetchingSummaryReports = false;
    },
    goToViewDetail(account) {
      const accountTypeProperties = getAccounTypeProperties(account.accountType);
      const routeParams = {
        name: accountTypeProperties.component,
        params: {
          accountId: account.id,
          name: this.name,
          userId: this.userId,
        },
      };

      this.$router.push(routeParams);
    },
    changeDefaultAccount(account) {
      this.showQuestionModal({
        title: 'Default Account',
        question: `Are you sure you want to select ${account.accountName} as new default account?`,
        method: () => {
          this.confirmChangeDefaultAccount(account.id);
        },
        yesButtonText: Constants.purchaseNote.modalYesButtonText,
        hideCancelButton: false,
      });
    },
    async confirmChangeDefaultAccount(id) {
      this.$emit(
        'confirmedChangeDefaultAccount',
        { accountId: id, userId: this.userId },
      );
      this.hideQuestionModal();
    },
  },
  components: {
    spinner,
  },
};
</script>

<style scoped>
  .line-separator {
    padding: 8px;
    line-height: 1.52857143;
    vertical-align: top;
    border-top: 1px solid #eee;
  }

  label {
    color: #888;
  }

  .lbl-defaultAccount {
    color: #888;
    font-size: 14px;
    width: 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 0px;
  }

  .top-margin {
    margin-top: 2%;
  }

  .panel-title {
    position: relative;
    width: 100%;
    padding-bottom: 20px;
    border-bottom: 1px dashed #eee;
  }

  .checkbox + .checkbox {
    margin-top: 20px;
  }

  .checkbox {
    cursor: default;
  }

  .checkbox label {
    position: relative;
    padding-left: 40px;
    cursor: pointer;
  }

  .c-checkbox:hover span {
    border-color: #cccccc;
  }

  .c-checkbox label:hover span {
    border-color: #137d8f;
  }

  .c-checkbox input:focus + span {
    border-color: #137d8f;
  }

  .checkbox .fa-check {
    position: absolute;
    margin: 0;
    left: 0;
  }

  .checkbox-disabled {
    outline: 1px solid red;
  }

  .checkbox p {
    margin-left: 40px;
    display: inline-block;
    cursor: text;
    color: #929292;
  }

  .checkbox-link {
    margin-left: 4px;
    display: inline-block;
    color: #137d8f;
    opacity: 0.75;
    transition: color 0.4s ease-in-out;
  }

  .checkbox-link:hover,
  .checkbox-link:focus {
    opacity: 1;
  }

  .extra-option {
    padding: 20px 0;
  }

  .extra-option textarea {
    resize: vertical;
  }

  .empty-state {
    margin: 0 auto;
    max-width: 740px;
    text-align: center;
    padding: 40px 0;
  }

  .empty__icon {
    font-size: 120px;
  }

  .empty__title {
    font-size: 28px;
    margin-bottom: 20px;
  }
  form.row {
    padding: 0 15px;
  }
  input.warning {
    border-color: #ffd814;
  }
  input.warning-error {
    border-color: #ff8116;
  }
  .panel-subtitle {
    font-weight: bold;
    font-size: 16px;
  }

  .green-counter {
    color: #558C1D;
  }

  .yellow-counter {
    color: #E58A2D;
  }

  .red-counter {
    color: #8C1D1D;
  }

  .radio-item {
    height: 25px;
    padding: 0;
  }

  .radio-item label {
    color: #666;
    font-weight: normal;
    padding-left: 25px;
    padding-top: 5px;
    cursor: pointer;
  }
  .radio-item label:before {
    content: ' ';
    display: inline-block;
    position: absolute;
    top: 5px;
    left: 0px;
    margin: 0 5px 0 0;
    width: 20px;
    height: 20px;
    border-radius: 11px;
    background-color: transparent;
  }
  .radio-item-unselected label:before {
    border: 2px solid #d3d3d3;
  }
  .radio-item label:after {
    border-radius: 5px;
    width: 10px;
    height: 10px;
    position: absolute;
    top: 10px;
    left: 0;
    content: '';
    display: block;
    background-color: #127285;
    margin-left: 5px;
  }

  .radio-item.radio-item-unselected label:after {
    display: none;
  }

  .last-column-data {
    width: 9%;
  }

  .last-column-title {
    width: 29%;
  }

  .table-spinner {
    position: relative;
  }
</style>



// WEBPACK FOOTER //
// AccountsList.vue?26af4728